import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PrintIcon from "@mui/icons-material/Print";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import { apiHelper, baseUrl } from "helpers/util";
import { notify } from "helpers/globalFunctions";
import { API_ENDPOINTS } from "config/apiConfig";
import html2canvas from "html2canvas";
import folkLogo from "images/folklogo.png";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";

export default function EventAttendance() {
    console.log("EventAttendance is rendering");

    const [mobileNo, setMobileNo] = useState("");
    const [getRecordId, setRecordId] = useState({});
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [fetchedRecord, setFetchedRecord] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorColor, setErrorColor] = useState("");
    const [totalPresentCount, setTotalPresentCount] = useState(null);
    const [dinnerPresentCount, setDinnerPresentCount] = useState(null);
    const [eventPresentCount, setEventPresentCount] = useState(null);

    const fetchTotalCount = async () => {
        try {
            const response = await apiHelper.get({
                url: `${baseUrl}${API_ENDPOINTS.EVENT_ATTENDANCE}`,
                params: { action: "count" },
            });
            if (response.status && response.json && response.json.count !== undefined) {
                setTotalPresentCount(response.json.count);
                setDinnerPresentCount(response.json.dinnerCount);
                setEventPresentCount(response.json.eventCount);
            }
        } catch (error) {
            console.error("Failed to fetch total count", error);
        }
    };

    useEffect(() => {
        fetchTotalCount();
        const intervalId = setInterval(fetchTotalCount, 15000); // Auto-refresh every 15 seconds
        return () => clearInterval(intervalId);
    }, []);

    const formErrorState = useRef({});
    const printState = useRef({ printCharacteristic: null, index: 0, data: null, canvas: null });

    // Auto-search effect
    useEffect(() => {
        if (!mobileNo) return;

        if (mobileNo.length === 10) {
            // Trigger immediately for 10 digits
            searchByMobileNo(mobileNo);
        }
    }, [mobileNo]);

    const resetForm = function () {
        setMobileNo("");
        setFetchedRecord(null);
        setErrorMessage(null);
        setErrorColor("");
        setRecordId({});
        setAreOptionsLoading(false);
        setIsFormSubmitting(false);

        // Clear print content
        const printContent = document.getElementById("print-content");
        if (printContent) {
            printContent.innerHTML = "";
        }

        // Clear form errors
        formErrorState.current = {};

        // Clear print state
        printState.current = { printCharacteristic: null, index: 0, data: null, canvas: null };
    };

    const resetDom = function () {
        setFetchedRecord(null);
        //document.getElementById("print-content").innerHTML = "";
    };

    const searchByMobileNo = async (mobileNo) => {
        try {
            setAreOptionsLoading(true);
            setErrorMessage(null);
            resetDom();

            // Log the request details for debugging
            console.log("Making API request with mobile:", mobileNo);

            const response = await apiHelper.get({
                url: `${baseUrl}${API_ENDPOINTS.EVENT_ATTENDANCE}`,
                params: { search_text: mobileNo },
            });

            // Log the response for debugging
            console.debug("API Response:", JSON.stringify(response));

            if (response.status) {
                if (!response.json) {
                    throw new Error("Response json is undefined");
                }
                if (response.json.length === 0) {
                    setErrorMessage(
                        "Oops! It looks like you haven't completed the registration yet. Kindly do so. Hare Krishna."
                    );
                    setErrorColor("error"); // Material-UI color for error messages
                    return; // Early exit if no records found
                }
                console.log("response json: " + response.json[0]["Record ID"])
                setRecordId(response.json[0]["Record ID"]);
                setFetchedRecord(response.json[0]);
            } else {
                setErrorMessage(`API returned status false: ${JSON.stringify(response.json)}`);
                setErrorColor("error");
            }
        } catch (error) {
            // Detailed error logging
            console.error("Error in searchByMobileNo:", {
                message: error.message,
                stack: error.stack,
                line: error.lineNumber,
                fileName: error.fileName,
            });
            setErrorMessage("An error occurred while searching.");
            setErrorColor("error");
        } finally {
            setAreOptionsLoading(false);
        }
    };

    const checkForFormErrors = (field, isError) => {
        formErrorState.current[field] = isError;
        return isError;
    };

    const handleError = function (error) {
        console.log(error);
        printState.current.printCharacteristic = null;
    };


    // Function to check if the pixel should be printed black
    const getDarkPixel = function (imageData, x, y) {
        // Return the pixels that will be printed black
        let red = imageData[(printState.current.canvas.width * y + x) * 4];
        let green = imageData[(printState.current.canvas.width * y + x) * 4 + 1];
        let blue = imageData[(printState.current.canvas.width * y + x) * 4 + 2];
        let alpha = imageData[(printState.current.canvas.width * y + x) * 4 + 3];
        if (alpha < 10) return 0;
        return red + green + blue > 730 ? 0 : 1;
    };
    const getImagePrintData = function (imageData) {
        if (imageData == null) {
            console.log("No image to print!");
            return new Uint8Array([]);
        }
        // Each 8 pixels in a row is represented by a byte
        let printData = new Uint8Array((printState.current.canvas.width / 8) * printState.current.canvas.height + 8);
        let offset = 0;
        // Set the header bytes for printing the image
        printData[0] = 29; // Print raster bitmap
        printData[1] = 118; // Print raster bitmap
        printData[2] = 48; // Print raster bitmap
        printData[3] = 0; // Normal 203.2 DPI
        printData[4] = printState.current.canvas.width / 8; // Number of horizontal printState.current.data bits (LSB)
        printData[5] = 0; // Number of horizontal printState.current.data bits (MSB)
        printData[6] = printState.current.canvas.height % 256; // Number of vertical printState.current.data bits (LSB)
        printData[7] = printState.current.canvas.height / 256; // Number of vertical printState.current.data bits (MSB)
        offset = 7;
        // Loop through image rows in bytes
        for (let i = 0; i < printState.current.canvas.height; ++i) {
            for (let k = 0; k < printState.current.canvas.width / 8; ++k) {
                let k8 = k * 8;
                //  Pixel to bit position mapping
                printData[++offset] =
                    getDarkPixel(imageData, k8 + 0, i) * 128 +
                    getDarkPixel(imageData, k8 + 1, i) * 64 +
                    getDarkPixel(imageData, k8 + 2, i) * 32 +
                    getDarkPixel(imageData, k8 + 3, i) * 16 +
                    getDarkPixel(imageData, k8 + 4, i) * 8 +
                    getDarkPixel(imageData, k8 + 5, i) * 4 +
                    getDarkPixel(imageData, k8 + 6, i) * 2 +
                    getDarkPixel(imageData, k8 + 7, i);
            }
        }
        return printData;
    };
    const sendImageData = function (imageData) {
        printState.current.index = 0;
        printState.current.data = getImagePrintData(imageData);
        return new Promise(function (resolve, reject) {
            sendNextImageDataBatch(resolve, reject);
        });
    };

    const sendNextImageDataBatch = function (resolve, reject) {
        // Can only write 512 bytes at a time to the characteristic
        // Need to send the image printState.current.data in 512 byte batches
        if (printState.current.index + 512 < printState.current.data.length) {
            printState.current.printCharacteristic
                .writeValueWithoutResponse(
                    printState.current.data.slice(printState.current.index, printState.current.index + 512)
                )
                .then(() => {
                    printState.current.index += 512;
                    sendNextImageDataBatch(resolve, reject);
                })
                .catch((error) => reject(error));
        } else {
            // Send the last bytes
            if (printState.current.index < printState.current.data.length) {
                printState.current.printCharacteristic
                    .writeValue(printState.current.data.slice(printState.current.index, printState.current.data.length))
                    .then(() => {
                        resolve();
                    })
                    .catch((error) => reject(error));
            } else {
                resolve();
            }
        }
    };

    // Function triggered on clicking print slip
    const onClickPrintSlip = () => {
        console.log("on click print slip method get called..");

        if (!fetchedRecord) {
            notify.error({ description: "No record loaded. Search a mobile number first." });
            return;
        }

        if (
            !window.AndroidApp &&
            (!printState.current.printCharacteristic ||
                !(printState.current.server && printState.current.server.connected))
        ) {
            notify.error({ description: "Connect to Printer first" });
            return;
        }

        if (Object.values(formErrorState.current).some((isError) => isError)) {
            notify.error({ description: "Please fill all details" });
            return;
        }

        handlePrintSlip(fetchedRecord);
    };

    const handlePrintSlip = async (fetchedRecord) => {
        console.log("handle Print slip get called...");
        setIsFormSubmitting(true);

        try {
            document.querySelector("body").classList.add("print");
            document.getElementById("print-content").innerHTML = `
                <div style="width: 100%; height: 1px; background-color: black; margin-top: 16px;"></div>
                <table style="width: 100%; margin-top: 24px; font-size: 22px; font-family: Arial, sans-serif; line-height: 34px; border-collapse: collapse;">
                    <tr>
                        <td style="width: 110px; padding-bottom: 8px; vertical-align: top;">Name</td>
                        <td style="width: 20px; padding-bottom: 8px; vertical-align: top;">:</td>
                        <td style="padding-bottom: 8px; vertical-align: top; font-weight: bold;">${fetchedRecord["Name"] || ""}</td>
                    </tr>
                    <tr>
                        <td style="padding-bottom: 8px; vertical-align: top;">E.Name</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">:</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">Heritage Fest 2026</td>
                    </tr>
                    <!-- 
                    <tr>
                        <td style="padding-bottom: 8px; vertical-align: top;">F.Guide</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">:</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">${fetchedRecord["FG Name"] || "-"}</td>
                    </tr>
                    <tr>
                        <td style="padding-bottom: 8px; vertical-align: top;">Guide No.</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">:</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">${fetchedRecord["FG Mobile"] || "-"}</td>
                    </tr>
                    -->
                    <tr>
                        <td style="padding-bottom: 8px; vertical-align: top;">Date</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">:</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">${dateStr}</td>
                    </tr>
                    <tr>
                        <td style="padding-bottom: 8px; vertical-align: top;">Time</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">:</td>
                        <td style="padding-bottom: 8px; vertical-align: top;">${timeStr.toUpperCase()}</td>
                    </tr>
                </table>

                <div style="text-align: center; margin-top: 34px; font-size: 22px; font-family: Arial, sans-serif; font-weight: bold; width: 100%;">
                    Hare Krishna !!<br/>Have a nice Event
                </div>

                <br/>
            `;

            document.getElementById("print-slip").classList.add("print");
            const printNode = document.getElementById("print-slip");

            await new Promise((resolve) => requestAnimationFrame(() => resolve()));

            const canvas = await html2canvas(printNode, {
                width: printNode.offsetWidth,
                height: printNode.offsetHeight + 40,
                scale: 1,
            });

            printState.current.canvas = canvas;
            let context = printState.current.canvas.getContext("2d");
            const imageDataUrl = canvas.toDataURL("image/jpeg").split(";base64,")[1];
            //console.log(imageDataUrl);

            // Send the image data to the Android app or backend
            if (window.AndroidApp) {
                window.AndroidApp.printPrasadam(imageDataUrl);
            } else if (printState.current.printCharacteristic) {
                let imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
                await sendImageData(imageData);
            } else {
                console.log("Bluetooth printer not connected - skipping print for local testing");
            }

            // Mark attendance after successful printing
            await markAttendance(getRecordId, fetchedRecord["Event Name"]);

        } catch (error) {
            console.error("Error in handlePrintSlip:", error);
            alert("Printing failed. Please try again.");
        } finally {
            // Clean up
            document.querySelector("body").classList.remove("print");
            document.getElementById("print-slip").classList.remove("print");
            document.getElementById("print-content").innerHTML = "";
            setIsFormSubmitting(false);
            resetForm();
        }
    };

    const markAttendance = async (recordId, eventName) => {
        console.log("markAttendance called...");
        console.log("Record ID:", recordId);

        // --- OLD AIRTABLE PAYLOAD (Commented for future reference) ---
        /*
        const payload = {
            record_id: Array.isArray(recordId) ? recordId[0] : recordId,
            event_name: eventName || "Event",
        };
        */

        // --- NEW GOOGLE SHEETS PAYLOAD ---
        const payload = {
            mobile: mobileNo, // Uses the mobile number from your component state
            attendanceTime: new Date().toISOString()
        };

        console.log("Payload:", payload);

        try {
            const response = await apiHelper.post({
                url: `${baseUrl}${API_ENDPOINTS.EVENT_ATTENDANCE}`,
                data: payload,
            });

            if (response.status) {
                console.log("Attendance marked successfully");
                notify.success({ description: "Attendance recorded successfully!" });
                resetForm();
                fetchTotalCount();
            } else {
                console.error("Error marking attendance:", response);
                notify.error({ description: "Failed to record attendance. Please try again." });
            }
        } catch (error) {
            console.error("Error in markAttendance:", error);
            notify.error({ description: "Something went wrong while recording attendance." });
        }
    };

    const currTime = new Date();
    const dateStr = currTime.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
    const timeStr = currTime.toLocaleString("en-IN", {
        hour12: true,
        hour: "2-digit",
        minute: "2-digit",
    });

    const participantPhotoUrl =
        fetchedRecord?.Photo?.[0]?.thumbnails?.large?.url ||
        fetchedRecord?.Photo?.[0]?.thumbnails?.full?.url ||
        fetchedRecord?.Photo?.[0]?.url ||
        "";

    const paymentStatus = fetchedRecord ? (fetchedRecord["Payment Status"] || "PENDING").toUpperCase() : "PENDING";
    const isSuccess = paymentStatus === "SUCCESS" || paymentStatus === "PAID";
    const statusColor = isSuccess ? "#15803d" : "#b91c1c";
    const statusBgColor = isSuccess ? "#dcfce7" : "#fee2e2";
    const statusIconColor = isSuccess ? "#22c55e" : "#ef4444";
    const statusIconShadow = isSuccess ? "0 0 20px rgba(34, 197, 94, 0.8), 0 0 35px rgba(34, 197, 94, 0.5)" : "0 0 20px rgba(239, 68, 68, 0.8), 0 0 35px rgba(239, 68, 68, 0.5)";
    const statusIcon = isSuccess ? "✔" : "✖";

    const amountVal = fetchedRecord ? Number(fetchedRecord["Amount"]) || 0 : 0;
    let privilegesText = "-";
    let privilegesColor = "#6b7280";
    let privilegesBg = "transparent";
    let privilegesBorder = "none";
    let privilegesIconBg = "transparent";
    let privilegesGlow = "none";

    if (amountVal === 99) {
        privilegesText = "Only Event";
        privilegesColor = "#0284c7"; // blue
        privilegesBg = "#f0f9ff";
        privilegesBorder = "2px solid #7dd3fc";
        privilegesIconBg = "#e0f2fe";
        privilegesGlow = "0 8px 25px rgba(56, 189, 248, 0.35)";
    } else if (amountVal === 199) {
        privilegesText = "Event + Dinner Coupon";
        privilegesColor = "#c2410c"; // orange
        privilegesBg = "#fff7ed";
        privilegesBorder = "2px solid #fdba74";
        privilegesIconBg = "#ffedd5";
        privilegesGlow = "0 8px 25px rgba(249, 115, 22, 0.3)";
    } else if (amountVal > 0) {
        privilegesText = "Standard Privileges";
        privilegesColor = "#6d28d9"; // purple
        privilegesBg = "#faf5ff";
        privilegesBorder = "2px solid #c4b5fd";
        privilegesIconBg = "#f3e8ff";
        privilegesGlow = "0 8px 25px rgba(168, 85, 247, 0.3)";
    }

    return (
        <Box sx={{
            minHeight: "100vh",
            width: "100%",
            maxWidth: "100vw",
            position: 'relative',
            overflowX: 'hidden',
            backgroundColor: "#f5f7fa",
            backgroundImage: `
                radial-gradient(circle at 10% 20%, rgba(253, 224, 71, 0.25) 0%, transparent 40%),
                radial-gradient(circle at 90% 80%, rgba(236, 72, 153, 0.25) 0%, transparent 40%),
                radial-gradient(circle at 15% 85%, rgba(168, 85, 247, 0.2) 0%, transparent 40%),
                radial-gradient(circle at 85% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 40%)
            `,
            pt: 4,
            pb: 8
        }}>
            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');
                `}
            </style>
            <Box sx={{
                width: "100%",
                maxWidth: {
                    xs: "100%",
                    sm: "500px",
                    md: "700px"
                },
                boxSizing: "border-box",
                mx: 'auto',
                px: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}>
                {/* Header Banner */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        mb: 1,
                    }}
                >
                    <Box
                        component="img"
                        src={folkLogo}
                        alt="FOLK EXCLUSIVE"
                        sx={{
                            height: { xs: 45, sm: 60 },
                            width: "auto",
                            mb: 3
                        }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <Box sx={{ height: '1px', width: '30px', backgroundColor: '#6366f1' }}></Box>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: "#4f46e5",
                                textTransform: "uppercase",
                                letterSpacing: "2px",
                                fontSize: "14px"
                            }}
                        >
                            Event Attendance Dashboard
                        </Typography>
                        <Box sx={{ height: '1px', width: '30px', backgroundColor: '#6366f1' }}></Box>
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: "'Caveat', cursive",
                            fontWeight: 700,
                            color: "#4c1d95",
                            lineHeight: 1.1,
                            fontSize: { xs: "36px", sm: "52px", md: "64px" },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            width: "100%",
                            textAlign: "center",
                            wordBreak: "break-word"
                        }}
                    >
                        <span>Heritage Fest 2026</span>
                        <span style={{
                            fontFamily: "Arial, sans-serif",
                            background: "linear-gradient(to right, #f97316, #ec4899)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            fontSize: "0.85em",
                            fontWeight: 900
                        }}></span>
                    </Typography>

                    {/* Prominent Total Count Box */}
                    <Box sx={{ 
                        mt: 2, 
                        mb: 4, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center' 
                    }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#6b7280", letterSpacing: "1px", textTransform: "uppercase" }}>
                            Total Present
                        </Typography>
                        <Box sx={{ 
                            mt: 0.5,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            borderRadius: "16px",
                            padding: "8px 24px",
                            boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)"
                        }}>
                            <Typography sx={{ 
                                fontSize: { xs: "32px", md: "42px" }, 
                                fontWeight: 900, 
                                color: "#ffffff", 
                                lineHeight: 1 
                            }}>
                                {totalPresentCount !== null ? totalPresentCount : <CircularProgress size={30} color="inherit" />}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", letterSpacing: "1px", textTransform: "uppercase" }}>
                                    Dinner
                                </Typography>
                                <Typography sx={{ fontSize: "24px", fontWeight: 800, color: "#10b981" }}>
                                    {dinnerPresentCount !== null ? dinnerPresentCount : "-"}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", letterSpacing: "1px", textTransform: "uppercase" }}>
                                    Event
                                </Typography>
                                <Typography sx={{ fontSize: "24px", fontWeight: 800, color: "#10b981" }}>
                                    {eventPresentCount !== null ? eventPresentCount : "-"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Typography
                        sx={{
                            mt: 2,
                            color: "#4b5563",
                            fontWeight: 700,
                            fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        }}
                    >
                        Search Participant Registration
                    </Typography>
                </Box>

                {/* Search Card */}
                <Box sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "32px",
                    border: "1px solid rgba(255,255,255,0.4)",
                    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.05)",
                    p: { xs: 2.5, sm: 4 },
                    mt: 1,
                }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <TextField
                            fullWidth
                            placeholder="Enter mobile number to find registration"
                            value={mobileNo}
                            onChange={(event) => setMobileNo(event.target.value)}
                            error={checkForFormErrors("mobileNo", !mobileNo)}
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneAndroidIcon sx={{ color: "#6366f1" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: areOptionsLoading && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} color="primary" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: "16px",
                                    backgroundColor: "#fff",
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e2e8f0',
                                }
                            }}
                        />
                    </Box>
                </Box>

                {/* Error Message */}
                {errorMessage && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: "16px",
                        backgroundColor: errorColor === "error" ? "#fee2e2" : "#e0e7ff",
                        color: errorColor === "error" ? "#991b1b" : "#3730a3",
                        border: `1px solid ${errorColor === "error" ? "#f87171" : "#818cf8"}`,
                        textAlign: "center",
                        fontWeight: 600
                    }}>
                        {errorMessage}
                    </Box>
                )}

                {/* User Card */}
                {fetchedRecord && (
                    <Box sx={{
                        backgroundColor: "#fff",
                        borderRadius: "28px",
                        boxShadow: "0 20px 50px rgba(15, 23, 42, 0.08)",
                        p: 3,
                    }}>
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "center", sm: "flex-start" }, gap: 3, mb: 3 }}>
                            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, gap: 3 }}>
                                <Box sx={{ width: 90, height: 90, borderRadius: "50%", border: "4px solid #8B5CF6", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#ede9fe" }}>
                                    {participantPhotoUrl ? (
                                        <Box
                                            component="img"
                                            src={participantPhotoUrl}
                                            alt="Participant Photo"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                objectPosition: "center top",
                                                display: "block",
                                                borderRadius: "50%",
                                            }}
                                        />
                                    ) : (
                                        <Box component="span" sx={{ fontSize: 38, color: "#5b21b6" }}>👤</Box>
                                    )}
                                </Box>
                                <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                                    <Box sx={{ fontSize: "20px", fontWeight: 800 }}>{fetchedRecord["Name"]}</Box>
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 1, px: 1.4, py: 0.75, borderRadius: "999px", backgroundColor: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: "12px" }}>
                                        <Box component="span">✔</Box>
                                        Registration Found
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: { xs: "center", sm: "right" }, borderTop: { xs: "1px solid #e2e8f0", sm: "none" }, pt: { xs: 2, sm: 0 }, width: { xs: "100%", sm: "auto" } }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-end' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: { xs: "36px", sm: "42px" }, fontWeight: 900, color: "#111827", lineHeight: 1, fontFamily: "Arial, sans-serif" }}>
                                            {fetchedRecord["Amount"] || "0"}
                                        </Typography>
                                        <Box component="span" sx={{
                                            fontSize: { xs: "36px", sm: "42px" },
                                            color: statusIconColor,
                                            lineHeight: 1,
                                            textShadow: statusIconShadow,
                                            fontWeight: 900,
                                            ml: 0.5
                                        }}>
                                            {statusIcon}
                                        </Box>
                                    </Box>
                                    {amountVal > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 0.5 }}>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                color: privilegesColor,
                                                backgroundColor: privilegesBg,
                                                border: privilegesBorder,
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '8px',
                                                boxShadow: privilegesGlow,
                                                fontSize: "12px",
                                                fontWeight: 800,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>
                                                <CardGiftcardIcon sx={{ fontSize: '16px' }} />
                                                {privilegesText}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <Card sx={{ borderRadius: "22px", boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <PersonIcon sx={{ color: "#7C3AED" }} />
                                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280" }}>Guide</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>{fetchedRecord["FG Name"] || "-"}</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ borderRadius: "22px", boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <EventIcon sx={{ color: "#f59e0b" }} />
                                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280" }}>Event</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>Heritage Fest 2026</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ borderRadius: "22px", boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <WorkOutlineIcon sx={{ color: "#10b981" }} />
                                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280" }}>Occupation</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>{fetchedRecord["Occupation"] || "-"}</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ borderRadius: "22px", boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <CalendarTodayIcon sx={{ color: "#2563eb" }} />
                                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280" }}>Date</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>{fetchedRecord["Timestamp"] ? new Date(fetchedRecord["Timestamp"]).toLocaleDateString("en-GB") : "-"}</Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                )}


                {/* Print Button / Attendance Status */}
                {fetchedRecord && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {fetchedRecord["Attendance Time"] ? (
                            <Box sx={{
                                width: { xs: "90%", sm: "220px", md: "400px" },
                                mx: 'auto',
                                borderRadius: "22px",
                                background: "#dcfce7",
                                border: "1px solid #86efac",
                                color: "#15803d",
                                textAlign: "center",
                                fontWeight: 800,
                                py: 2,
                                fontSize: "16px",
                                boxShadow: "0 10px 25px rgba(21, 128, 61, 0.15)"
                            }}>
                                ✅ ATTENDANCE ALREADY MARKED
                            </Box>
                        ) : (
                            <Button
                                onClick={onClickPrintSlip}
                                variant="contained"
                                disabled={isFormSubmitting}
                                startIcon={<PrintIcon />}
                                sx={{
                                    width: {
                                        xs: "90%",
                                        sm: "220px",
                                        md: "400px"
                                    },
                                    mx: 'auto',
                                    borderRadius: "22px",
                                    background: "linear-gradient(135deg, #7C3AED, #2563EB)",
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                    py: 1.8,
                                    fontSize: "15px",
                                    boxShadow: "0 16px 30px rgba(99, 102, 241, 0.24)",
                                    '&:hover': {
                                        background: "linear-gradient(135deg, #6D28D9, #1D4ED8)",
                                    }
                                }}
                            >
                                {isFormSubmitting ? <CircularProgress size={24} color="inherit" /> : "PRINT ENTRY PASS"}
                            </Button>
                        )}
                    </Box>
                )}

                {/* Hidden print slip content */}
                <div
                    id="print-slip"
                    style={{
                        position: "absolute",
                        left: "-10000px",
                        top: 0,
                        width: "384px",
                        background: "white",
                        textAlign: "center",
                        padding: "12px",
                        boxSizing: "border-box",
                        pointerEvents: "none",
                        fontFamily: "Arial, sans-serif",
                        color: "black"
                    }}
                >
                    <img src={folkLogo} alt="FOLK Logo" style={{ width: "100px", marginBottom: "16px" }} />
                    <div style={{ fontSize: "22px", fontWeight: "bold", textAlign: "center" }}>
                        HERITAGE FEST 2026
                    </div>
                    <div
                        id="print-content"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            background: "white",
                            width: "100%",
                        }}
                    ></div>
                </div>
            </Box>
        </Box>
    );
}
